<template>
	<div>
		<div class="gh-layout">
			<aside class="gh-intro">
				<h2 class="gh-intro__title">ADA Audits</h2>
				<p class="gh-intro__desc">
					Overview of ADA audit activity. <strong>Reports</strong> shows how many audit reports exist per
					client — ad-hoc audits without a client are listed by website. <strong>Global warnings</strong>
					are rules acknowledged for <em>every</em> client and URL: matching findings are hidden from all
					future scans. Removing one makes the finding surface again on the next scan.
					<strong>Test a URL</strong> runs the scanners against any address and shows the result once —
					nothing is written to reports or scan history.
				</p>
			</aside>

			<section class="gh-accounts">
				<!-- Ad-hoc URL test — scan any address, show the result, save nothing -->
				<div class="ada-card mb-3">
					<div class="gh-section">
						<div class="aud-head">
							<div class="gh-section__title mb-0">
								<i class="fas fa-vial me-1"></i> Test a URL
							</div>
						</div>
						<div class="gh-help">
							Runs the selected scanners against any public URL and opens the findings in a modal.
							Nothing is stored — no report, no scan row, no client link.
						</div>

						<form class="aud-test" @submit.prevent="runTest">
							<div class="aud-test__row">
								<input
									type="url"
									class="form-control form-control-sm aud-test__url"
									v-model.trim="testUrl"
									placeholder="https://example.gov/page"
									:disabled="testing"
									required
								/>
								<button class="gh-save-btn gh-save-btn--sm" type="submit" :disabled="testing || !testUrl">
									<i class="fas" :class="testing ? 'fa-spinner fa-spin' : 'fa-play'"></i>
									{{ testing ? 'Scanning…' : 'Run scan' }}
								</button>
							</div>

							<div class="aud-test__engines">
								<label v-for="opt in engineOptions" :key="opt.value" class="aud-test__engine" :title="opt.hint">
									<input type="checkbox" :value="opt.value" v-model="testEngines" :disabled="testing" />
									<span>{{ opt.label }}</span>
								</label>
								<label
									class="aud-test__engine aud-test__engine--sep"
									title="Hide findings acknowledged globally, the way a real scan would"
								>
									<input type="checkbox" v-model="testApplyWarnings" :disabled="testing" />
									<span>Apply global warnings</span>
								</label>
							</div>

							<div v-if="testing" class="aud-test__hint">
								<i class="fas fa-spinner fa-spin me-1"></i>
								Scanning {{ testUrl }} — a page with many linked PDFs can take a couple of minutes.
							</div>
							<div v-if="testError" class="text-danger aud-test__hint">{{ testError }}</div>
						</form>
					</div>
				</div>

				<!-- Reports per client / website -->
				<div class="ada-card mb-3">
					<div class="gh-section">
						<div class="aud-head">
							<div class="gh-section__title mb-0">
								<i class="fas fa-file-alt me-1"></i> Reports
								<span v-if="!loadingReports" class="aud-head__meta">
									{{ filteredReports.length }} of {{ reports.length }} sites · {{ totalReports }} reports
								</span>
							</div>
							<input
								type="text"
								class="form-control form-control-sm aud-search"
								v-model.trim="reportSearch"
								placeholder="Search client or website…"
							/>
						</div>

						<div v-if="loadingReports" class="text-muted mt-2">Loading…</div>
						<div v-else-if="reportsError" class="text-danger mt-2">{{ reportsError }}</div>
						<div v-else-if="!filteredReports.length" class="text-muted mt-2">No audit reports yet.</div>

						<div v-else class="aud-table-wrap mt-2">
							<table class="aud-table">
								<thead>
									<tr>
										<th class="aud-sort" @click="setSort('name')">
											Client / website <i class="fas" :class="sortIcon('name')"></i>
										</th>
										<th class="aud-sort text-end" @click="setSort('reports')">
											Reports <i class="fas" :class="sortIcon('reports')"></i>
										</th>
										<th class="aud-sort" @click="setSort('last_report_at')">
											Last report <i class="fas" :class="sortIcon('last_report_at')"></i>
										</th>
										<th class="text-end">Score</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="r in visibleReports" :key="rowKey(r)">
										<td class="aud-row-cell">
											<span class="aud-client aud-client--plain">{{ r.website || '—' }}</span>
											<span class="aud-muted">—</span>
											<template v-if="r.client_id">
												<router-link class="aud-client" :to="`/juniper/clients/edit/${r.client_id}/settings`">
													{{ r.name || 'Client' }}
												</router-link>
												<span class="aud-muted">#{{ r.client_id }}</span>
												<span v-if="r.client_status && r.client_status !== 'live'" class="aud-muted">
													({{ r.client_status }})
												</span>
											</template>
											<span v-else class="aud-noclient">NOT CLIENT</span>
										</td>
										<td class="text-end">{{ r.reports }}</td>
										<td :title="r.last_report_at || ''">
											{{ r.last_report_at ? fromNowUtc(r.last_report_at) : '—' }}
											<span v-if="r.last_status === 'working'" class="aud-muted">(running)</span>
										</td>
										<td class="text-end">
											<span v-if="r.last_score != null" class="aud-score" :class="scoreClass(r.last_score)">
												{{ r.last_score }}
											</span>
											<span v-else class="aud-muted">—</span>
										</td>
									</tr>
								</tbody>
							</table>

							<div v-if="visibleReports.length < filteredReports.length" class="aud-load-more-row">
								<button type="button" class="aud-load-more-btn" @click="reportLimit += 10">Load more</button>
								<small class="text-muted">{{ visibleReports.length }} of {{ filteredReports.length }}</small>
							</div>
						</div>
					</div>
				</div>

				<!-- Global warnings -->
				<div class="ada-card mb-3">
					<div class="gh-section">
						<div class="aud-head">
							<div class="gh-section__title mb-0">
								<i class="fas fa-globe me-1"></i> Global warnings
								<span v-if="!loadingWarnings" class="aud-head__meta">{{ warnings.length }} rules</span>
							</div>
							<button class="gh-save-btn gh-save-btn--sm" :disabled="loadingWarnings" @click="loadWarnings">
								<i class="fas fa-sync me-1"></i> Reload
							</button>
						</div>
						<div class="gh-help">
							Rules dismissed or resolved globally — hidden from scans for all clients and URLs.
						</div>

						<div v-if="loadingWarnings" class="text-muted mt-2">Loading…</div>
						<div v-else-if="warningsError" class="text-danger mt-2">{{ warningsError }}</div>
						<div v-else-if="!warnings.length" class="text-muted mt-2">No global warnings set.</div>

						<div v-else class="aud-table-wrap mt-2">
							<table class="aud-table">
								<thead>
									<tr>
										<th>Engine</th>
										<th>Rule</th>
										<th>Severity</th>
										<th>Status</th>
										<th>Note</th>
										<th>Added</th>
										<th></th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="w in visibleWarnings" :key="w.id" class="aud-row-click" @click="openDetails(w)">
										<td><span class="aud-engine">{{ w.engine }}</span></td>
										<td class="aud-rule">{{ w.rule_id }}</td>
										<td>
											<span class="aud-badge" :class="w.bucket === 'error' ? 'aud-badge--error' : 'aud-badge--warn'">
												{{ w.bucket }}
											</span>
										</td>
										<td>
											<template v-if="editingId === w.id">
												<select v-model="editStatus" class="form-select form-select-sm aud-edit-status">
													<option value="dismissed">dismissed</option>
													<option value="resolved">resolved</option>
												</select>
											</template>
											<span v-else class="aud-badge" :class="w.status === 'resolved' ? 'aud-badge--resolved' : 'aud-badge--dismissed'">
												{{ w.status }}
											</span>
										</td>
										<td class="aud-note">
											<template v-if="editingId === w.id">
												<input
													type="text"
													class="form-control form-control-sm"
													v-model="editNote"
													placeholder="Why is this not a warning?"
													@keyup.enter="saveWarning(w)"
												/>
											</template>
											<span v-else :title="w.note || ''">{{ w.note || '—' }}</span>
										</td>
										<td class="aud-added">
											<i
												class="fas fa-user-circle aud-added__user"
												data-bs-toggle="tooltip"
												data-bs-placement="top"
												:title="ackTooltip(w)"
											></i>
											<span class="aud-muted" :title="w.created_at || ''">
												{{ w.created_at ? fromNowUtc(w.created_at) : '—' }}
											</span>
										</td>
										<td class="aud-actions" @click.stop>
											<template v-if="editingId === w.id">
												<button class="gh-save-btn gh-save-btn--sm" :disabled="savingId === w.id" @click="saveWarning(w)">
													<i class="fas" :class="savingId === w.id ? 'fa-spinner fa-spin' : 'fa-check'"></i> Save
												</button>
												<button class="gh-save-btn gh-save-btn--sm" :disabled="savingId === w.id" @click="editingId = null">
													Cancel
												</button>
											</template>
											<template v-else>
												<button class="aud-icon-btn" title="Edit note / status" @click="startEdit(w)">
													<i class="fas fa-pen"></i>
												</button>
												<button class="aud-icon-btn aud-icon-btn--danger" title="Remove — finding re-surfaces on next scan" @click="askDelete(w)">
													<i class="fas fa-trash-alt"></i>
												</button>
											</template>
										</td>
									</tr>
								</tbody>
							</table>

							<div v-if="visibleWarnings.length < warnings.length" class="aud-load-more-row">
								<button type="button" class="aud-load-more-btn" @click="warningLimit += 10">Load more</button>
								<small class="text-muted">{{ visibleWarnings.length }} of {{ warnings.length }}</small>
							</div>
						</div>
					</div>
				</div>
			</section>
		</div>

		<UrlTestModal v-if="testResult" :result="testResult" @close="testResult = null" />

		<!-- Global warning drill-down: what does this rule actually flag? -->
		<div ref="detailsModalEl" class="modal fade" tabindex="-1" aria-hidden="true">
			<div class="modal-dialog modal-dialog-centered modal-lg">
				<div class="modal-content awd-content" v-if="detailsWarning">
					<div class="modal-header awd-header">
						<h6 class="modal-title awd-title">
							<span class="aud-engine me-2">{{ detailsWarning.engine }}</span>
							<code class="awd-rule">{{ detailsWarning.rule_id }}</code>
							<span class="aud-badge ms-2" :class="detailsWarning.bucket === 'error' ? 'aud-badge--error' : 'aud-badge--warn'">
								{{ detailsWarning.bucket }}
							</span>
							<span class="aud-badge ms-1" :class="detailsWarning.status === 'resolved' ? 'aud-badge--resolved' : 'aud-badge--dismissed'">
								{{ detailsWarning.status }}
							</span>
						</h6>
						<button type="button" class="btn-close awd-close" @click="closeDetails"></button>
					</div>
					<div class="modal-body awd-body">
						<div v-if="detailsWarning.note" class="awd-note">
							<i class="fas fa-sticky-note me-1"></i> {{ detailsWarning.note }}
						</div>
						<div class="awd-meta">
							Added {{ detailsWarning.created_at ? fromNowUtc(detailsWarning.created_at) : '—' }}
							<template v-if="detailsWarning.acknowledged_by"> by {{ ackTooltip(detailsWarning) }}</template>
						</div>

						<div v-if="detailsLoading" class="text-muted py-3">
							<i class="fas fa-spinner fa-spin me-1"></i> Loading examples…
						</div>
						<div v-else-if="detailsError" class="text-danger py-3">{{ detailsError }}</div>
						<template v-else>
							<div v-if="detailsScan" class="awd-scan">
								<i class="fas fa-crosshairs me-1"></i>
								Examples from <a :href="detailsScan.url" target="_blank" rel="noopener">{{ detailsScan.url }}</a>
								<span class="aud-muted">(scan #{{ detailsScan.id }})</span>
							</div>

							<div v-if="!detailsExamples.length" class="text-muted py-2">
								No matching finding found in stored scans — the source scan was deleted and no recent scan
								hits this rule anymore.
							</div>
							<ul v-else class="awd-examples">
								<li v-for="(ex, i) in detailsExamples" :key="i" class="awd-example">
									<div class="awd-example__msg">{{ ex.message || ex.description || '—' }}</div>
									<div v-if="exampleLocation(ex)" class="awd-example__loc">
										<code>{{ exampleLocation(ex) }}</code>
									</div>
									<pre v-if="exampleSnippet(ex)" class="awd-example__snippet">{{ exampleSnippet(ex) }}</pre>
								</li>
							</ul>
						</template>
					</div>
					<div class="modal-footer awd-footer">
						<button type="button" class="gh-save-btn gh-save-btn--sm" @click="closeDetails">Close</button>
					</div>
				</div>
			</div>
		</div>

		<ConfirmModal
			ref="deleteWarningModal"
			title="Remove global warning?"
			:message="deleteMessage"
			confirm-text="Remove"
			confirm-icon="fa-trash-alt"
			icon-class="fa-trash-alt"
			danger
			:busy="deletingId != null"
			@confirm="confirmDelete"
			@cancel="pendingDelete = null"
		/>
	</div>
</template>

<script>
import { Modal, Tooltip } from 'bootstrap'
import api from '@/api'
import { showGlobalToast } from '@/utils/toast'
import { fromNowUtc } from '@/utils/time'
import ConfirmModal from '@/components/common/ConfirmModal.vue'
import UrlTestModal from '@/components/juniper/ada-auditor/UrlTestModal.vue'
import { runAdaTestScan, ADA_TEST_ENGINES, ADA_TEST_DEFAULT_ENGINES } from '@/utils/adaTestScan'

export default {
	name: 'AuditsSettings',
	components: { ConfirmModal, UrlTestModal },
	data() {
		return {
			// Reports section
			reports: [],
			loadingReports: true,
			reportsError: null,
			reportSearch: '',
			reportLimit: 10,
			sortBy: 'reports',
			sortDir: 'desc',

			// Global warnings section
			warnings: [],
			loadingWarnings: true,
			warningsError: null,
			warningLimit: 10,
			editingId: null,
			editNote: '',
			editStatus: 'dismissed',
			savingId: null,
			pendingDelete: null,
			deletingId: null,

			// Ad-hoc URL test (nothing persisted — the result lives in this component only)
			testUrl: '',
			// TWAC is the always-on baseline in real audits, so it starts checked here too.
			testEngines: [...ADA_TEST_DEFAULT_ENGINES],
			testApplyWarnings: false,
			testing: false,
			testError: null,
			testResult: null,

			// Details modal (row click drill-down)
			detailsWarning: null,
			detailsScan: null,
			detailsExamples: [],
			detailsLoading: false,
			detailsError: null,
		}
	},
	computed: {
engineOptions() {
			return ADA_TEST_ENGINES
		},
				totalReports() {
			return this.reports.reduce((sum, r) => sum + (r.reports || 0), 0)
		},
		filteredReports() {
			let rows = this.reports
			const q = this.reportSearch.toLowerCase()
			if (q) {
				rows = rows.filter(
					(r) => (r.name || '').toLowerCase().includes(q) || (r.website || '').toLowerCase().includes(q)
				)
			}
			const dir = this.sortDir === 'desc' ? -1 : 1
			const key = this.sortBy
			return [...rows].sort((a, b) => {
				let av = a[key]
				let bv = b[key]
				if (key === 'name') {
					av = (a.name || a.website || '').toLowerCase()
					bv = (b.name || b.website || '').toLowerCase()
				}
				if (av == null && bv == null) return 0
				if (av == null) return 1
				if (bv == null) return -1
				if (av < bv) return -1 * dir
				if (av > bv) return 1 * dir
				return 0
			})
		},
		visibleReports() {
			return this.filteredReports.slice(0, this.reportLimit)
		},
		visibleWarnings() {
			return this.warnings.slice(0, this.warningLimit)
		},
		deleteMessage() {
			if (!this.pendingDelete) return ''
			return `${this.pendingDelete.engine} · ${this.pendingDelete.rule_id} will no longer be hidden — it re-surfaces on the next scan for every client.`
		},
	},
	watch: {
		visibleWarnings() {
			this.initTooltips()
		},
	},
	created() {
		this.loadReports()
		this.loadWarnings()
	},
	mounted() {
		this.detailsModal = new Modal(this.$refs.detailsModalEl)
	},
	beforeDestroy() {
		if (this.detailsModal) this.detailsModal.dispose()
	},
	methods: {
		fromNowUtc,
		rowKey(r) {
			return r.client_id ? `c-${r.client_id}` : `w-${r.website}`
		},
		scoreClass(score) {
			if (score >= 90) return 'aud-score--good'
			if (score >= 70) return 'aud-score--warn'
			return 'aud-score--bad'
		},
		setSort(key) {
			if (this.sortBy === key) {
				this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc'
			} else {
				this.sortBy = key
				this.sortDir = key === 'name' ? 'asc' : 'desc'
			}
		},
		sortIcon(key) {
			if (this.sortBy !== key) return 'fa-sort aud-sort__idle'
			return this.sortDir === 'desc' ? 'fa-sort-down' : 'fa-sort-up'
		},
		ackTooltip(w) {
			const by = w.acknowledged_by
			if (!by) return 'Unknown user'
			const name = by.name || by.email || 'Unknown user'
			return by.email && by.name ? `${name} · ${by.email}` : name
		},
		// (Re)attach Bootstrap tooltips on the warnings table. Rows re-render on
		// reload/load-more, so stale instances are disposed first.
		initTooltips() {
			this.$nextTick(() => {
				this.$el.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
					const existing = Tooltip.getInstance(el)
					if (existing) existing.dispose()
					new Tooltip(el)
				})
			})
		},

		// Ad-hoc scan of any URL. The endpoint runs the engines and returns the
		// findings inline — it persists nothing, so there is no polling and no
		// report to clean up afterwards.
async runTest() {
			if (this.testing || !this.testUrl) return
			if (!this.testEngines.length) {
				this.testError = 'Pick at least one scanner.'
				return
			}
			this.testing = true
			this.testError = null
			this.testResult = null
			try {
				this.testResult = await runAdaTestScan({
					url: this.testUrl,
					engines: this.testEngines,
					applyWarnings: this.testApplyWarnings,
				})
			} catch (e) {
				console.error('test scan failed', e)
				this.testError = e.message
			} finally {
				this.testing = false
			}
		},

				async loadReports() {
			this.loadingReports = true
			this.reportsError = null
			try {
				const res = await api.get('/juniper/ada-auditor/stats/reports')
				this.reports = res.data.data || []
			} catch (e) {
				this.reportsError = 'Failed to load report stats.'
				console.error('audit report stats failed', e)
			} finally {
				this.loadingReports = false
			}
		},

		async loadWarnings() {
			this.loadingWarnings = true
			this.warningsError = null
			try {
				const res = await api.get('/juniper/ada-auditor/warnings', { params: { is_global: 1 } })
				this.warnings = res.data.global || []
			} catch (e) {
				this.warningsError = 'Failed to load global warnings.'
				console.error('global warnings load failed', e)
			} finally {
				this.loadingWarnings = false
			}
		},

		async openDetails(w) {
			if (this.editingId === w.id) return
			this.detailsWarning = w
			this.detailsScan = null
			this.detailsExamples = []
			this.detailsError = null
			this.detailsLoading = true
			this.detailsModal.show()
			try {
				const res = await api.get(`/juniper/ada-auditor/warnings/${w.id}/details`)
				this.detailsScan = res.data.scan
				this.detailsExamples = res.data.examples || []
			} catch (e) {
				console.error('warning details failed', e)
				this.detailsError = 'Failed to load details.'
			} finally {
				this.detailsLoading = false
			}
		},
		closeDetails() {
			this.detailsModal.hide()
		},
		exampleLocation(ex) {
			const raw = ex.raw || {}
			const sel = Array.isArray(ex.selectors) ? ex.selectors.join(', ') : ex.selectors
			return ex.xpath || ex.selector || sel || ex.target || raw.xpath || raw.target || ''
		},
		exampleSnippet(ex) {
			return ex.snippet || (ex.raw && ex.raw.snippet) || ''
		},

		startEdit(w) {
			this.editingId = w.id
			this.editNote = w.note || ''
			this.editStatus = w.status || 'dismissed'
		},
		async saveWarning(w) {
			this.savingId = w.id
			try {
				const res = await api.patch(`/juniper/ada-auditor/warnings/${w.id}`, {
					note: this.editNote,
					status: this.editStatus,
				})
				const updated = res.data.warning
				const i = this.warnings.findIndex((x) => x.id === w.id)
				if (i !== -1) this.$set(this.warnings, i, updated)
				this.editingId = null
				showGlobalToast('Warning updated', 'success')
			} catch (e) {
				console.error('warning update failed', e)
				showGlobalToast('Failed to update warning', 'error')
			} finally {
				this.savingId = null
			}
		},

		askDelete(w) {
			this.pendingDelete = w
			this.$refs.deleteWarningModal.open()
		},
		async confirmDelete() {
			if (!this.pendingDelete) return
			this.deletingId = this.pendingDelete.id
			try {
				await api.delete(`/juniper/ada-auditor/warnings/${this.pendingDelete.id}`)
				this.warnings = this.warnings.filter((x) => x.id !== this.pendingDelete.id)
				showGlobalToast('Global warning removed', 'success')
				this.$refs.deleteWarningModal.close()
			} catch (e) {
				console.error('warning delete failed', e)
				showGlobalToast('Failed to remove warning', 'error')
			} finally {
				this.deletingId = null
				this.pendingDelete = null
			}
		},
	},
}
</script>

<style scoped lang="scss">
$aud-border: #f1f3f5;
$aud-muted: #8a919c;

.aud-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.75rem;
	flex-wrap: wrap;
}
.aud-head__meta {
	font-size: 0.75rem;
	font-weight: 400;
	text-transform: none;
	letter-spacing: 0;
	color: $aud-muted;
	margin-left: 0.5rem;
}
.aud-search {
	max-width: 200px;
	font-size: 0.78rem;
	padding: 0.25rem 0.6rem;
	background: transparent;
	border: 1px solid #e6e8ec;
	border-radius: 7px;
	box-shadow: none;

	&:focus {
		background: transparent;
		border-color: #cfd4dc;
		box-shadow: none;
	}
	&::placeholder {
		color: #adb5bd;
	}
}

/* Ad-hoc URL test form */
.aud-test {
	margin-top: 0.6rem;
}
.aud-test__row {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}
.aud-test__url {
	flex: 1;
	font-size: 0.82rem;
	padding: 0.3rem 0.6rem;
	background: transparent;
	border: 1px solid #e6e8ec;
	border-radius: 7px;
	box-shadow: none;

	&:focus {
		background: transparent;
		border-color: #cfd4dc;
		box-shadow: none;
	}
}
.aud-test__engines {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 0.85rem;
	margin-top: 0.55rem;
}
.aud-test__engine {
	display: inline-flex;
	align-items: center;
	gap: 0.3rem;
	font-size: 0.78rem;
	color: #5b6472;
	margin: 0;
	cursor: pointer;
}
.aud-test__engine--sep {
	margin-left: auto;
	color: $aud-muted;
}
.aud-test__hint {
	margin-top: 0.5rem;
	font-size: 0.78rem;
	color: $aud-muted;
}

.aud-table-wrap {
	overflow-x: auto;
}
.aud-table {
	width: 100%;
	font-size: 13px;
	border-collapse: collapse;

	th {
		font-weight: 500;
		font-size: 12px;
		color: $aud-muted;
		text-align: left;
		padding: 0.35rem 0.5rem;
		border-bottom: 1px solid $aud-border;
		white-space: nowrap;
	}
	td {
		padding: 0.45rem 0.5rem;
		border-bottom: 1px solid $aud-border;
		vertical-align: middle;
	}
	tbody tr:last-child td {
		border-bottom: none;
	}
}
.aud-sort {
	cursor: pointer;
	user-select: none;
	.aud-sort__idle {
		opacity: 0.35;
	}
}

.aud-client {
	font-weight: 500;
	color: #2b303b;
	text-decoration: none;
	&:hover {
		text-decoration: underline;
	}
}
.aud-client--plain {
	font-weight: 500;
}
.aud-row-cell {
	white-space: nowrap;
	max-width: 480px;
	overflow: hidden;
	text-overflow: ellipsis;

	> * + * {
		margin-left: 0.3rem;
	}
}
.aud-noclient {
	display: inline-block;
	font-size: 10.5px;
	font-weight: 600;
	letter-spacing: 0.04em;
	padding: 0.1rem 0.45rem;
	border-radius: 10px;
	background: #f0f2f5;
	color: #5b6472;
}
.aud-site {
	font-size: 11.5px;
	color: $aud-muted;
	max-width: 340px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.aud-muted {
	color: $aud-muted;
	font-size: 12px;
}

.aud-score {
	font-weight: 600;
	&.aud-score--good {
		color: #1a9e55;
	}
	&.aud-score--warn {
		color: #d9930d;
	}
	&.aud-score--bad {
		color: #d64545;
	}
}
/* Load-more action row — one compact centered line: [Load more] 5 of 12,
   same style as juniper Audits / Deploy / Hooks tables. */
.aud-load-more-row {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 0.75rem;
	padding: 0.9rem 0 0.25rem;
}
.aud-load-more-btn {
	display: inline-flex;
	align-items: center;
	gap: 0.3rem;
	background: none;
	border: 0;
	padding: 0;
	color: #adb5bd;
	font-size: 0.82rem;
	font-weight: 600;
	cursor: pointer;
	transition: color 0.12s ease;

	&:hover:not(:disabled) {
		color: #2b303b;
	}
	&:disabled {
		opacity: 0.55;
		cursor: default;
	}
}

.aud-added {
	white-space: nowrap;
}
.aud-added__user {
	color: #adb5bd;
	font-size: 15px;
	margin-right: 0.35rem;
	cursor: default;
	vertical-align: middle;
}

.aud-engine {
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.03em;
}
.aud-rule {
	font-family: var(--bs-font-monospace, monospace);
	font-size: 12px;
	max-width: 260px;
	overflow-wrap: anywhere;
}
.aud-note {
	max-width: 280px;
	span {
		display: inline-block;
		max-width: 280px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		vertical-align: bottom;
	}
}
.aud-badge {
	display: inline-block;
	font-size: 11px;
	font-weight: 600;
	padding: 0.1rem 0.45rem;
	border-radius: 10px;

	&.aud-badge--error {
		background: #fdecec;
		color: #d64545;
	}
	&.aud-badge--warn {
		background: #fdf3e0;
		color: #b47a0b;
	}
	&.aud-badge--dismissed {
		background: #f0f2f5;
		color: #5b6472;
	}
	&.aud-badge--resolved {
		background: #e7f6ee;
		color: #1a9e55;
	}
}
.aud-edit-status {
	font-size: 0.78rem;
	width: auto;
	display: inline-block;
}

.aud-actions {
	white-space: nowrap;
	text-align: right;
	.gh-save-btn + .gh-save-btn {
		margin-left: 0.3rem;
	}
}
.aud-row-click {
	cursor: pointer;
	&:hover td {
		background: #fafbfc;
	}
}

/* Details modal */
.awd-content {
	border: 1px solid #e6e8ec;
	border-radius: 14px;
}
.awd-header {
	border-bottom: 1px solid #f1f3f5;
	padding: 0.75rem 1rem;
}
.awd-title {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	font-size: 0.9rem;
}
.awd-rule {
	font-size: 0.82rem;
	color: #2b303b;
}
.awd-close {
	font-size: 0.7rem;
}
.awd-body {
	padding: 0.9rem 1rem;
	font-size: 13px;
}
.awd-note {
	background: #f8f9fb;
	border: 1px solid #f1f3f5;
	border-radius: 8px;
	padding: 0.5rem 0.7rem;
	margin-bottom: 0.5rem;
}
.awd-meta {
	color: $aud-muted;
	font-size: 12px;
	margin-bottom: 0.75rem;
}
.awd-scan {
	font-size: 12.5px;
	margin-bottom: 0.6rem;
	a {
		color: #2b303b;
		overflow-wrap: anywhere;
	}
}
.awd-examples {
	list-style: none;
	margin: 0;
	padding: 0;
}
.awd-example {
	padding: 0.6rem 0;
	border-top: 1px solid #f1f3f5;
}
.awd-example__msg {
	font-weight: 500;
}
.awd-example__loc {
	margin-top: 0.25rem;
	code {
		font-size: 11.5px;
		color: $aud-muted;
		overflow-wrap: anywhere;
	}
}
.awd-example__snippet {
	margin: 0.35rem 0 0;
	padding: 0.45rem 0.6rem;
	background: #f8f9fb;
	border: 1px solid #f1f3f5;
	border-radius: 8px;
	font-size: 11.5px;
	white-space: pre-wrap;
	overflow-wrap: anywhere;
	max-height: 140px;
	overflow-y: auto;
}
.awd-footer {
	border-top: 1px solid #f1f3f5;
	padding: 0.6rem 1rem;
}

.aud-icon-btn {
	border: none;
	background: none;
	color: #adb5bd;
	padding: 0.15rem 0.3rem;
	&:hover {
		color: #2b303b;
	}
	&.aud-icon-btn--danger:hover {
		color: #d64545;
	}
}
</style>
